import { getPhaseDuration, getPhaseName, PHASE_ORDER } from '../generators/generate-preview-schedule-data'
import { parseIntervalToDays } from '~/common/utils/date-utils';

/**
 * Helper function to handle adding/removing stratification phase
 * Preserves user drag adjustments by inserting/removing at the beginning
 */
export const handleStratificationToggle = (adjustedPhases, isAdding, phaseTypes) => {
  if (isAdding) {
    // Insert stratification at the beginning
    const firstPhase = adjustedPhases[0]
    if (!firstPhase) return adjustedPhases

    const stratificationDuration = getPhaseDuration('stratification', phaseTypes)
    const firstStart = new Date(firstPhase.est_started_at)
    const stratificationStart = new Date(firstStart)
    stratificationStart.setDate(stratificationStart.getDate() - stratificationDuration)

    const newPhase = {
      id: 'preview-phase-strat',
      phase_type_code: 'stratification',
      phase_type: {
        code: 'stratification',
        name: getPhaseName('stratification', phaseTypes),
        typical_duration_days: stratificationDuration
      },
      est_started_at: stratificationStart.toISOString(),
      est_duration: `${stratificationDuration} days`,
      started_at: null,
      duration: null,
      step: 1,
      state: 'scheduled'
    }

    // Update step numbers for existing phases
    const updatedPhases = adjustedPhases.map(p => ({
      ...p,
      step: p.step + 1
    }))

    return [newPhase, ...updatedPhases]
  } else {
    // Remove stratification and renumber steps
    return adjustedPhases
      .filter(p => p.phase_type_code !== 'stratification')
      .map((p, idx) => ({ ...p, step: idx + 1 }))
  }
}

/**
 * Helper function to handle adding/removing dormant phase (perennials only)
 * Appends/removes the closing dormant phase at the end.
 * Duration extends to Dec 31 of the schedule year.
 */
export const handleDormantToggle = (adjustedPhases, isAdding, phaseTypes) => {
  if (isAdding) {
    const lastPhase = adjustedPhases[adjustedPhases.length - 1]
    if (!lastPhase) return adjustedPhases

    const lastStart = new Date(lastPhase.est_started_at)
    const lastDuration = parseIntervalToDays(lastPhase.est_duration)
    const dormantStart = new Date(lastStart)
    dormantStart.setDate(dormantStart.getDate() + lastDuration)

    // Extend to Dec 31 of the schedule year
    const yearEnd = new Date(dormantStart.getFullYear(), 11, 31)
    const dormantDays = Math.max(1, Math.floor((yearEnd - dormantStart) / (1000 * 60 * 60 * 24)))

    const newPhase = {
      id: 'preview-phase-dormant',
      phase_type_code: 'dormant',
      phase_type: {
        code: 'dormant',
        name: getPhaseName('dormant', phaseTypes),
        typical_duration_days: dormantDays
      },
      est_started_at: dormantStart.toISOString(),
      est_duration: `${dormantDays} days`,
      started_at: null,
      duration: null,
      step: adjustedPhases.length + 1,
      state: 'scheduled'
    }

    return [...adjustedPhases, newPhase]
  } else {
    // Remove the closing dormant (last dormant in the list)
    let lastDormantIdx = -1
    for (let i = adjustedPhases.length - 1; i >= 0; i--) {
      if (adjustedPhases[i].phase_type_code === 'dormant') {
        lastDormantIdx = i
        break
      }
    }
    if (lastDormantIdx === -1) return adjustedPhases
    return adjustedPhases.filter((_, idx) => idx !== lastDormantIdx)
  }
}

/**
 * Handle adding/removing any non-boundary phase while preserving schedule position.
 * Inserts the new phase at the correct PHASE_ORDER slot, shifting subsequent phases
 * forward (add) or closing the gap (remove).
 *
 * Handles subsequent-year perennials that have both an opening dormant (first phase)
 * and a closing dormant (last phase) — the opener is held in place.
 */
export const computePhasesAfterToggle = (existingPhases, phaseCode, isAdding, phaseTypes) => {
  if (!existingPhases || existingPhases.length === 0) return existingPhases

  // Subsequent-year perennial: first AND last phase are both 'dormant'
  const hasOpeningDormant =
    existingPhases[0]?.phase_type_code === 'dormant' &&
    existingPhases.length > 1 &&
    existingPhases[existingPhases.length - 1]?.phase_type_code === 'dormant'

  const opener = hasOpeningDormant ? existingPhases[0] : null
  const working = hasOpeningDormant ? existingPhases.slice(1) : existingPhases

  const result = isAdding
    ? insertPhaseIntoList(working, phaseCode, phaseTypes)
    : removePhaseFromList(working, phaseCode)

  if (opener) {
    return [
      { ...opener, step: 1 },
      ...result.map((p, i) => ({ ...p, step: i + 2 })),
    ]
  }
  return result.map((p, i) => ({ ...p, step: i + 1 }))
}

function insertPhaseIntoList(phases, phaseCode, phaseTypes) {
  const newOrder = PHASE_ORDER.indexOf(phaseCode)

  // Find the first phase whose PHASE_ORDER position is after the new phase
  let insertIdx = phases.length
  for (let i = 0; i < phases.length; i++) {
    if (PHASE_ORDER.indexOf(phases[i].phase_type_code) > newOrder) {
      insertIdx = i
      break
    }
  }

  // New phase starts immediately after the preceding phase ends
  let newStart
  if (insertIdx > 0) {
    const prev = phases[insertIdx - 1]
    const prevDays = parseIntervalToDays(prev.est_duration)
    newStart = new Date(new Date(prev.est_started_at).getTime() + prevDays * 86400000)
  } else if (phases.length > 0) {
    newStart = new Date(phases[0].est_started_at)
  } else {
    newStart = new Date()
  }

  const duration = getPhaseDuration(phaseCode, phaseTypes)
  const durationMs = duration * 86400000

  const newPhase = {
    id: `preview-phase-${phaseCode}`,
    phase_type_code: phaseCode,
    phase_type: {
      code: phaseCode,
      name: getPhaseName(phaseCode, phaseTypes),
      typical_duration_days: duration,
    },
    est_started_at: newStart.toISOString(),
    est_duration: `${duration} days`,
    started_at: null,
    duration: null,
    step: insertIdx + 1,
    state: 'scheduled',
  }

  return [
    ...phases.slice(0, insertIdx),
    newPhase,
    ...phases.slice(insertIdx).map(p => ({
      ...p,
      est_started_at: new Date(new Date(p.est_started_at).getTime() + durationMs).toISOString(),
    })),
  ]
}

function removePhaseFromList(phases, phaseCode) {
  const removeIdx = phases.findIndex(p => p.phase_type_code === phaseCode)
  if (removeIdx === -1) return phases

  const removedMs = parseIntervalToDays(phases[removeIdx].est_duration) * 86400000

  return [
    ...phases.slice(0, removeIdx),
    ...phases.slice(removeIdx + 1).map(p => ({
      ...p,
      est_started_at: new Date(new Date(p.est_started_at).getTime() - removedMs).toISOString(),
    })),
  ]
}

/**
 * Helper function to handle adding/removing decomposition phase
 * Preserves user drag adjustments by appending/removing at the end
 */
export const handleDecompositionToggle = (adjustedPhases, isAdding, phaseTypes) => {
  if (isAdding) {
    // Append decomposition at the end
    const lastPhase = adjustedPhases[adjustedPhases.length - 1]
    if (!lastPhase) return adjustedPhases

    const decompositionDuration = getPhaseDuration('decomposition', phaseTypes)
    const lastStart = new Date(lastPhase.est_started_at)
    const lastDuration = parseIntervalToDays(lastPhase.est_duration)
    const decompositionStart = new Date(lastStart)
    decompositionStart.setDate(decompositionStart.getDate() + lastDuration)

    const newPhase = {
      id: 'preview-phase-decomp',
      phase_type_code: 'decomposition',
      phase_type: {
        code: 'decomposition',
        name: getPhaseName('decomposition', phaseTypes),
        typical_duration_days: decompositionDuration
      },
      est_started_at: decompositionStart.toISOString(),
      est_duration: `${decompositionDuration} days`,
      started_at: null,
      duration: null,
      step: adjustedPhases.length + 1,
      state: 'scheduled'
    }

    return [...adjustedPhases, newPhase]
  } else {
    // Remove decomposition
    return adjustedPhases.filter(p => p.phase_type_code !== 'decomposition')
  }
}
